/**
 * Single agent hooks — detail fetch + update mutation.
 *
 * The backend sends a `version` (HEAD sha over the config) inside the
 * agent body; we thread that through the mutation as the If-Match token.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentsApi, type UpdateAgentPayload } from '@/api/agents';

export function useAgent(name: string | undefined) {
  return useQuery({
    queryKey: ['agent', name],
    queryFn: () => agentsApi.get(name!),
    enabled: !!name,
    staleTime: 5_000,
  });
}

export function useUpdateAgent(name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, payload }: { version: string; payload: UpdateAgentPayload }) =>
      agentsApi.update(name, version, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent', name] });
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}
